// Dependencies
import { format, isSameISOWeek, isSameMonth } from 'date-fns';
import * as locales from 'date-fns/locale';
import i18next from 'i18next';
import React, { FC, Fragment, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
// Core & Lab
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
// Components
import ChannelManagerStockAction from './channelManagerStockAction';
import ChannelManagerStockCalendarClosedSaleCell from './channelManagerStockCalendarClosedSaleCell';
// import ChannelManagerStockCalendarRemainingCell from './channelManagerStockCalendarRemainingCell';
// import ChannelManagerStockCalendarSoldCell from './channelManagerStockCalendarSoldCell';
import ChannelManagerStockCalendarStockCell from './channelManagerStockCalendarStockCell';
import ChannelManagerStockClosedSaleSave from './channelManagerStockClosedSaleSave';
import ChannelManagerStockEditClosedSale from './channelManagerStockEditClosedSale';
// import ChannelManagerStockEditOptions from './channelManagerStockEditOptions';
// Utils
import LocaleText from '../utils/localeText/localeText';
// Types
import { Box } from '@mui/system';
import { AppState } from '../../reducers';
import { ChannelManagerStockColorLegend } from './channelManagerStockColorLegend';
import { Room } from './objects/room';
import { Stocks } from './objects/stocks';
import { Divider, IconButton, Typography } from '@mui/material';
import { Edit } from '@mui/icons-material';
import ChannelManagerStockCalendarSoldCell from './channelManagerStockCalendarSoldCell';
import ChannelManagerStockEditOptions from './channelManagerStockEditOptions';

const ChannelManagerStockCalendar: FC<{ setDateCriteriaOpen: React.Dispatch<React.SetStateAction<boolean>>; }> = ({ setDateCriteriaOpen }): ReactElement | null => {
    const { t } = useTranslation();
    const locales_cpy: { [key: string]: any } = locales;
    const stocks_display_mode = useSelector((state: AppState) => state.channelManager.stocks_display_mode);
    const stocks_state = useSelector((state: AppState) => state.channelManager.stocks_state);
    const date_criteria = useSelector((state: AppState) => state.channelManager.date_criteria);
    const onWheel = (event: { deltaY: number; }) => {
        const container = document.getElementById('manual-accommodation-stocks');
        if (container !== null) {
            container.scrollLeft += event.deltaY;
        }
    };
    const monthDisplay = (state: { dates: number[]; rooms?: (Room & { stocks: (Stocks | { id: null, date: number })[]; })[]; }) => {
        const cells: JSX.Element[] = [];
        let prev_date: null | number = null;
        let size = 0;
        state.dates.map((date, date_index) => {
            if (prev_date === null || isSameMonth(new Date(prev_date), new Date(date))) {
                size++;
            } else {
                cells.push(<TableCell key={`manual-accommodation-stocks-month-${cells.length}`} id={`manual-accommodation-stocks-month-${format(new Date(prev_date), 'M-yyyy')}`} className={cells.length > 0 ? 'ft-roomStockCell' : ''} align={'center'} colSpan={size}>{format(new Date(prev_date), 'MMMM yyy', { locale: locales_cpy[i18next.language.replace("-", "")] })}</TableCell>);
                size = 1;
            }
            prev_date = date;
        });
        if (prev_date !== null) {
            cells.push(<TableCell key={`manual-accommodation-stocks-month-${cells.length}`} id={`manual-accommodation-stocks-month-${format(new Date(prev_date), 'M-yyyy')}`} className={cells.length > 0 ? 'ft-roomStockCell' : ''} align={'center'} colSpan={size}>{format(new Date(prev_date), 'MMMM yyy', { locale: locales_cpy[i18next.language.replace("-", "")] })}</TableCell>);
        }
        return cells;
    };
    const weekDisplay = (state: { dates: number[]; rooms?: (Room & { stocks: (Stocks | { id: null, date: number })[]; })[]; }) => {
        const cells: JSX.Element[] = [];
        let prev_date: null | number = null;
        let size = 0;
        state.dates.map((date, date_index) => {
            if (prev_date === null || isSameISOWeek(new Date(prev_date), new Date(date))) {
                size++;
            } else {
                console.log('data : ', format(new Date(prev_date), 'dd w-yyyy'));
                cells.push(<TableCell key={`manual-accommodation-stocks-week-${cells.length}`} id={`manual-accommodation-stocks-week-${format(new Date(prev_date), 'w-yyyy')}`} className={cells.length > 0 ? 'ft-roomStockCell' : ''} align={'center'} colSpan={size}>{t('shared.week')} {cells.length + 1}</TableCell>);
                size = 1;
            }
            prev_date = date;
        });
        if (prev_date !== null) {
            cells.push(<TableCell key={`manual-accommodation-stocks-week-${cells.length}`} id={`manual-accommodation-stocks-week-${format(new Date(prev_date), 'w-yyyy')}`} className={cells.length > 0 ? 'ft-roomStockCell' : ''} align={'center'} colSpan={size}>{t('shared.week')} {cells.length + 1}</TableCell>);
        }
        return cells;
    };
    if (stocks_state !== null) {
        return (
            <Grid container spacing={2} sx={{ marginTop: 0 }}>
                <Grid item container alignItems={'center'}>
                    <Grid item>
                        <Typography variant={'h6'} component={'div'}>
                            {t('shared.stocks-state')} : {' '}
                            {
                                (!date_criteria.start_date || !date_criteria.end_date) ? t('shared.no-period-defined') : `${t('shared.from')} ${format(date_criteria.start_date, 'dd/MM/yyyy')} ${t('shared.To')} ${format(date_criteria.end_date, 'dd/MM/yyyy')}`
                            }
                        </Typography>
                    </Grid>
                    <Grid item><IconButton onClick={() => setDateCriteriaOpen(true)}><Edit /></IconButton></Grid>
                </Grid>
                <ChannelManagerStockAction />
                <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', padding: '10px 0' }}>
                    <ChannelManagerStockEditOptions/>
                    <ChannelManagerStockColorLegend />
                </div>
                <ChannelManagerStockEditClosedSale />
                <Grid item xs={12}>
                    <Paper className={'ft-list-paper'} sx={{ borderTop: 'solid rgba(224, 224, 224, 1) 1px' }}>
                        <TableContainer id={'manual-accommodation-stocks'} onWheel={onWheel}>
                            <Table className={'ft-list-table'} size={'small'}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={'ft-roomStockCellSticky'} />
                                        {stocks_display_mode === 0 ? monthDisplay(stocks_state) : weekDisplay(stocks_state)}
                                    </TableRow>
                                    <TableRow>
                                        <TableCell id={'manual-accommodation-stocks-name'} className={'ft-roomStockCellSticky'}>{t('shared.name')}</TableCell>
                                        {stocks_state.dates.map((date, date_index) => stocks_display_mode === 0 ? <TableCell key={`manual-accommodation-stocks-date-${date_index}`} id={`manual-accommodation-stocks-date-${format(new Date(date), 'D-yyyy', { useAdditionalDayOfYearTokens: true })}`}>{format(new Date(date), 'E dd', { locale: locales_cpy[i18next.language.replace("-", "")] })}</TableCell> : <TableCell key={`manual-accommodation-stocks-date-${date_index}`} id={`manual-accommodation-stocks-date-${format(new Date(date), 'D-yyyy', { useAdditionalDayOfYearTokens: true })}`} className={'ft-roomStockCellWeek'}>{format(new Date(date), 'eeee d  MMMM yyyy', { locale: locales_cpy[i18next.language.replace("-", "")] })}</TableCell>)}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        stocks_state.rooms.map(room => (
                                            <Fragment key={`manual-accommodation-room-${room.id}-stocks`}>
                                                <TableRow tabIndex={-1}>
                                                    <TableCell className={'ft-roomStockCellSticky ft-roomStockCellName'}><Box sx={{ fontWeight: 'bold' }}><LocaleText localization={room.localization} default_text={room.name} text_key={'name'} /></Box></TableCell>
                                                    {room.stocks.map((stock, stock_index) => <ChannelManagerStockCalendarClosedSaleCell key={`manual-accommodation-room-${room.id}-stocks-name-${stock_index}`} room_id={room.id} stock_index={stock_index} stock={stock} black_outs={room.black_out} />)}
                                                </TableRow>
                                                <TableRow tabIndex={-1}>
                                                    <TableCell className={'ft-roomStockCellSticky'}>{t('shared.stock')}</TableCell>
                                                    {room.stocks.map((stock, stock_index) => <ChannelManagerStockCalendarStockCell key={`manual-accommodation-room-${room.id}-stocks-stock-${stock_index}-cell`} room_id={room.id} stock_index={stock_index} stock={stock} />)}
                                                </TableRow>
                                                <TableRow tabIndex={ -1 }>
                                                    <TableCell className={ 'ft-roomStockCellSticky' }>{ t('shared.sold') }</TableCell>
                                                    { room.stocks.map((stock, stock_index) => <ChannelManagerStockCalendarSoldCell key={ `manual-accommodation-room-${room.id}-stocks-sold-${stock_index}-cell` } room_id={ room.id } stock_index={ stock_index } stock={ stock }/>) }
                                                </TableRow>
                                                <TableRow tabIndex={-1}>
                                                    <TableCell colSpan={367}><Divider /></TableCell>
                                                </TableRow>
                                                {/* <TableRow tabIndex={ -1 }>
                                                    <TableCell className={ 'ft-roomStockCellSticky' }>{ t('shared.remaining') }</TableCell>
                                                    { room.stocks.map((stock, stock_index) => <ChannelManagerStockCalendarRemainingCell key={ `manual-accommodation-room-${room.id}-stocks-remaining-${stock_index}-cell` } room_id={ room.id } stock_index={ stock_index } stock={ stock }/>) }
                                                </TableRow> */}
                                            </Fragment>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
                <ChannelManagerStockClosedSaleSave />
            </Grid>
        );
    }
    return <Grid container spacing={2} sx={{ marginTop: 0 }}>
        <Grid item container alignItems={'center'}>
            <Grid item>
                <Typography variant={'h6'} component={'div'}>
                    {t('shared.stocks-state')} : {' '}
                    {
                        (!date_criteria.start_date || !date_criteria.end_date) ? t('shared.no-period-defined') : `${t('shared.from')} ${format(date_criteria.start_date, 'dd/MM/yyyy')} ${t('shared.To')} ${format(date_criteria.end_date, 'dd/MM/yyyy')}`
                    }
                </Typography>
            </Grid>
            <Grid item><IconButton onClick={() => setDateCriteriaOpen(true)}><Edit /></IconButton></Grid>
        </Grid>
    </Grid>;
};

export default ChannelManagerStockCalendar;